/**
 * Combined Listings — Cross-product colour variant swatches
 *
 * Custom element that loads product groupings from a JSON config,
 * fetches sibling product data from the Shopify AJAX API, and
 * renders circle swatches using the theme's native .color-swatch
 * pattern so they look identical to built-in variant pickers.
 */

if (!customElements.get('combined-listings')) {
  class CombinedListings extends HTMLElement {
    /** Shared config cache (one fetch per page load) */
    static _configCache = null;
    static _configPromise = null;

    /** Shared product data cache */
    static _productCache = {};

    connectedCallback() {
      this._handle = this.getAttribute('product-handle');
      this._configUrl = this.getAttribute('config-url');

      if (!this._handle || !this._configUrl) return;
      this._init();
    }

    async _init() {
      try {
        const config = await this._fetchConfig();
        const group = this._findGroup(config);

        if (!group || group.products.length < 2) return;

        const products = await this._fetchProducts(group.products);
        const valid = products.filter(Boolean);

        if (valid.length < 2) return;

        this._render(group, valid);
      } catch (err) {
        /* Silently fail — the product page still works without swatches */
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Data fetching                                                      */
    /* ------------------------------------------------------------------ */

    async _fetchConfig() {
      if (CombinedListings._configCache) {
        return CombinedListings._configCache;
      }

      if (!CombinedListings._configPromise) {
        CombinedListings._configPromise = fetch(this._configUrl)
          .then(r => r.json())
          .then(data => {
            CombinedListings._configCache = data;
            return data;
          })
          .catch(err => {
            console.error('Combined listings config fetch failed:', err);
            CombinedListings._configPromise = null; // Allow retry on next call
            return null;
          });
      }

      return CombinedListings._configPromise;
    }

    _findGroup(config) {
      if (!config || !config.groups) return null;
      return config.groups.find(g =>
        g.products.some(p => p.handle === this._handle)
      );
    }

    async _fetchProducts(configProducts) {
      return Promise.all(
        configProducts.map(async cp => {
          try {
            if (!CombinedListings._productCache[cp.handle]) {
              const resp = await fetch('/products/' + cp.handle + '.js');
              if (!resp.ok) return null;
              CombinedListings._productCache[cp.handle] = await resp.json();
            }

            const data = CombinedListings._productCache[cp.handle];

            return {
              handle:       cp.handle,
              label:        cp.label || data.title,
              swatch_image: cp.swatch_image || '',
              title:        data.title,
              image:        data.featured_image,
              url:          '/products/' + cp.handle,
              isCurrent:    cp.handle === this._handle
            };
          } catch {
            return null;
          }
        })
      );
    }

    /* ------------------------------------------------------------------ */
    /*  Rendering                                                          */
    /* ------------------------------------------------------------------ */

    _render(group, products) {
      const currentProduct = products.find(p => p.isCurrent);
      const currentLabel = currentProduct ? currentProduct.label : '';
      const optionLabel  = group.label || 'Colour';

      /*
       * Mirror the theme's variant-picker HTML structure exactly
       * so that all theme CSS (spacing, font, responsive) applies.
       */
      const picker = document.createElement('div');
      picker.className = 'product-info__product-picker';
      picker.innerHTML =
        '<div class="variant-picker">' +
          '<fieldset class="variant-picker__option">' +
            '<div class="variant-picker__option-info">' +
              '<div class="h-stack gap-2">' +
                '<legend class="text-subdued">' + this._esc(optionLabel) + ':</legend>' +
                '<span>' + this._esc(currentLabel) + '</span>' +
              '</div>' +
            '</div>' +
            '<div class="variant-picker__option-values wrap gap-2"></div>' +
          '</fieldset>' +
        '</div>';

      const swatchWrap = picker.querySelector('.variant-picker__option-values');

      products.forEach(p => {
        swatchWrap.appendChild(this._createSwatch(p));
      });

      this.appendChild(picker);
      this.setAttribute('data-loaded', '');
    }

    _createSwatch(product) {
      const a = document.createElement('a');

      a.href = product.url;
      a.className = 'color-swatch rounded-full' + (product.isCurrent ? ' is-selected' : '');
      a.setAttribute('data-tooltip', product.label);
      a.setAttribute('aria-label', product.label);

      /* Use custom swatch image if set, otherwise fall back to product image */
      const imageUrl = product.swatch_image || product.image;
      if (imageUrl) {
        const sized = this._resizeImage(imageUrl, 200);
        a.style.setProperty('--swatch-background', 'url(' + sized + ')');
      }

      const sr = document.createElement('span');
      sr.className = 'sr-only';
      sr.textContent = product.label;
      a.appendChild(sr);

      return a;
    }

    /* ------------------------------------------------------------------ */
    /*  Helpers                                                            */
    /* ------------------------------------------------------------------ */

    /**
     * Append a width param to Shopify CDN image URLs for an optimised thumbnail.
     */
    _resizeImage(url, width) {
      if (!url) return url;
      try {
        const u = new URL(url, window.location.origin);
        u.searchParams.set('width', String(width));
        return u.toString();
      } catch {
        return url;
      }
    }

    /**
     * Basic HTML-safe escaping for user-supplied labels.
     */
    _esc(str) {
      if (!str) return '';
      var d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    }
  }

  customElements.define('combined-listings', CombinedListings);
}
